import React from "react";

/* ================================================================
   Abstract Concepts — 8 animated templates
   Time, cause-effect, pros-cons, growth, teamwork, leadership,
   innovation, life cycle
   Clean diagram style with conceptual animations
   ================================================================ */

// ── 1. Time Flow ─────────────────────────────────────────────────────
export const TimeFlow: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const hourAngle = (phase * 6) % 360;
  const minuteAngle = (phase * 60) % 360;
  return (
    <svg viewBox="0 0 500 450" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Clock face */}
      <circle cx="250" cy="200" r="120" fill="#0f172a" stroke="#6366f1" strokeWidth="3" />
      <circle cx="250" cy="200" r="115" fill="none" stroke="#334155" strokeWidth="1" />
      {/* Hour markers */}
      {Array.from({ length: 12 }).map((_, i) => {
        const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
        const x1 = 250 + 100 * Math.cos(a);
        const y1 = 200 + 100 * Math.sin(a);
        const x2 = 250 + 110 * Math.cos(a);
        const y2 = 200 + 110 * Math.sin(a);
        return (
          <g key={i}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#e0e7ff" strokeWidth={i % 3 === 0 ? 3 : 1.5} />
            {i % 3 === 0 && (
              <text x={250 + 88 * Math.cos(a)} y={204 + 88 * Math.sin(a)} textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">
                {i === 0 ? 12 : i}
              </text>
            )}
          </g>
        );
      })}
      {/* Hour hand */}
      <line x1="250" y1="200" x2={250 + 55 * Math.cos((hourAngle - 90) * Math.PI / 180)} y2={200 + 55 * Math.sin((hourAngle - 90) * Math.PI / 180)}
            stroke="#e0e7ff" strokeWidth="4" strokeLinecap="round" />
      {/* Minute hand */}
      <line x1="250" y1="200" x2={250 + 80 * Math.cos((minuteAngle - 90) * Math.PI / 180)} y2={200 + 80 * Math.sin((minuteAngle - 90) * Math.PI / 180)}
            stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" />
      {/* Center dot */}
      <circle cx="250" cy="200" r="5" fill="#6366f1" />
      {/* Hourglass sand */}
      <g transform="translate(250,380)">
        <polygon points="-15,-20 15,-20 5,0 -5,0" fill="#f59e0b" opacity="0.4" />
        <polygon points="-5,0 5,0 15,20 -15,20" fill="#f59e0b" opacity="0.2" />
        {/* Sand stream */}
        <line x1="0" y1="-2" x2="0" y2="2" stroke="#f59e0b" strokeWidth="1.5">
          <animate attributeName="opacity" values="0;1;0" dur="1s" repeatCount="indefinite" />
        </line>
      </g>
      <text x="250" y="430" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Time Flows</text>
    </svg>
  );
};

// ── 2. Cause and Effect ──────────────────────────────────────────────
export const CauseEffect: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  const items = [
    { cause: "Rain ☔", effect: "Flood 🌊" },
    { cause: "Study 📚", effect: "Knowledge 💡" },
    { cause: "Exercise 🏃", effect: "Health ❤️" },
    { cause: "Seed 🌱", effect: "Tree 🌳" },
  ];
  return (
    <svg viewBox="0 0 700 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <text x="350" y="35" textAnchor="middle" fill="#e0e7ff" fontSize="16" fontWeight="bold">Cause → Effect</text>
      {items.map((item, i) => {
        const y = 80 + i * 80;
        const isActive = i === activeIndex;
        return (
          <g key={i} opacity={isActive ? 1 : 0.35}>
            {/* Cause box */}
            <rect x="100" y={y} width="160" height="50" rx="8" fill="#1e3a8a" stroke={isActive ? "#3b82f6" : "#334155"} strokeWidth={isActive ? 2 : 1} />
            <text x="180" y={y + 30} textAnchor="middle" fill="#e0e7ff" fontSize="14">{item.cause}</text>
            {/* Arrow */}
            <line x1="270" y1={y + 25} x2="380" y2={y + 25} stroke={isActive ? "#f59e0b" : "#475569"} strokeWidth="2" markerEnd="url(#ceArrow)">
              {isActive && <animate attributeName="stroke-dashoffset" values="20;0" dur="1s" fill="freeze" />}
            </line>
            <text x="325" y={y + 18} textAnchor="middle" fill="#f59e0b" fontSize="10">→</text>
            {/* Effect box */}
            <rect x="390" y={y} width="160" height="50" rx="8" fill="#7f1d1d" stroke={isActive ? "#ef4444" : "#334155"} strokeWidth={isActive ? 2 : 1}>
              {isActive && <animate attributeName="opacity" values="0.5;1;0.5" dur="1.5s" repeatCount="indefinite" />}
            </rect>
            <text x="470" y={y + 30} textAnchor="middle" fill="#e0e7ff" fontSize="14">{item.effect}</text>
          </g>
        );
      })}
      <defs>
        <marker id="ceArrow" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 Z" fill="#f59e0b" />
        </marker>
      </defs>
    </svg>
  );
};

// ── 3. Pros and Cons ─────────────────────────────────────────────────
export const ProsCons: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  const pros = ["Fast", "Affordable", "Scalable"];
  const cons = ["Complex", "Learning curve", "Maintenance"];
  return (
    <svg viewBox="0 0 700 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <text x="350" y="35" textAnchor="middle" fill="#e0e7ff" fontSize="16" fontWeight="bold">Pros vs Cons</text>
      {/* Divider */}
      <line x1="350" y1="50" x2="350" y2="370" stroke="#475569" strokeWidth="2" strokeDasharray="5 3" />
      {/* Pros header */}
      <rect x="80" y="55" width="220" height="35" rx="6" fill="#166534" />
      <text x="190" y="78" textAnchor="middle" fill="#fff" fontSize="14" fontWeight="bold">✓ Pros</text>
      {/* Cons header */}
      <rect x="400" y="55" width="220" height="35" rx="6" fill="#7f1d1d" />
      <text x="510" y="78" textAnchor="middle" fill="#fff" fontSize="14" fontWeight="bold">✗ Cons</text>
      {/* Pro items */}
      {pros.map((p, i) => (
        <g key={`p-${i}`} opacity={activeIndex >= i ? 1 : 0.3}>
          <rect x="80" y={110 + i * 70} width="220" height="50" rx="6" fill="#0f172a" stroke={activeIndex === i ? "#22c55e" : "#334155"} strokeWidth={activeIndex === i ? 2 : 1}>
            {activeIndex === i && <animate attributeName="opacity" values="0.7;1;0.7" dur="1.5s" repeatCount="indefinite" />}
          </rect>
          <text x="100" y={140 + i * 70} fill="#22c55e" fontSize="14">✓</text>
          <text x="120" y={140 + i * 70} fill="#e0e7ff" fontSize="13">{p}</text>
        </g>
      ))}
      {/* Con items */}
      {cons.map((c, i) => (
        <g key={`c-${i}`} opacity={activeIndex >= i ? 1 : 0.3}>
          <rect x="400" y={110 + i * 70} width="220" height="50" rx="6" fill="#0f172a" stroke={activeIndex === i ? "#ef4444" : "#334155"} strokeWidth={activeIndex === i ? 2 : 1}>
            {activeIndex === i && <animate attributeName="opacity" values="0.7;1;0.7" dur="1.5s" repeatCount="indefinite" />}
          </rect>
          <text x="420" y={140 + i * 70} fill="#ef4444" fontSize="14">✗</text>
          <text x="440" y={140 + i * 70} fill="#e0e7ff" fontSize="13">{c}</text>
        </g>
      ))}
    </svg>
  );
};

// ── 4. Growth Chart ──────────────────────────────────────────────────
export const GrowthChart: React.FC<Record<string, unknown>> = (props) => {
  const flowProgress = (props.flowProgress as number) ?? 0;
  const data = [20, 35, 50, 55, 75, 90, 95];
  const maxVisible = Math.ceil(flowProgress * data.length);
  return (
    <svg viewBox="0 0 600 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <text x="300" y="30" textAnchor="middle" fill="#e0e7ff" fontSize="16" fontWeight="bold">Growth Over Time</text>
      {/* Axes */}
      <line x1="80" y1="330" x2="550" y2="330" stroke="#475569" strokeWidth="2" />
      <line x1="80" y1="330" x2="80" y2="60" stroke="#475569" strokeWidth="2" />
      {/* Y axis labels */}
      {[0, 25, 50, 75, 100].map((v, i) => (
        <g key={i}>
          <line x1="75" y1={330 - (v / 100) * 260} x2="80" y2={330 - (v / 100) * 260} stroke="#475569" strokeWidth="1" />
          <text x="65" y={335 - (v / 100) * 260} textAnchor="middle" fill="#94a3b8" fontSize="9">{v}%</text>
        </g>
      ))}
      {/* Bars */}
      {data.slice(0, maxVisible).map((v, i) => {
        const barH = (v / 100) * 260;
        const bx = 110 + i * 60;
        const isLast = i === maxVisible - 1;
        return (
          <g key={i}>
            <rect x={bx} y={330 - barH} width="40" height={barH} rx="4" fill={isLast ? "#6366f1" : "#3b82f6"} opacity={isLast ? 1 : 0.6}>
              {isLast && <animate attributeName="height" values={`${barH - 20};${barH};${barH}`} dur="0.5s" fill="freeze" />}
            </rect>
            <text x={bx + 20} y={320 - barH} textAnchor="middle" fill="#e0e7ff" fontSize="10" fontWeight="bold">{v}%</text>
            <text x={bx + 20} y="350" textAnchor="middle" fill="#94a3b8" fontSize="9">W{i + 1}</text>
          </g>
        );
      })}
      {/* Trend line */}
      <path d={data.slice(0, maxVisible).map((v, i) => {
        const x = 130 + i * 60;
        const y = 330 - (v / 100) * 260;
        return `${i === 0 ? "M" : "L"}${x},${y}`;
      }).join(" ")} fill="none" stroke="#22c55e" strokeWidth="2" strokeDasharray="4 2" />
      <text x="300" y="385" textAnchor="middle" fill="#22c55e" fontSize="12">↑ Upward Trend</text>
    </svg>
  );
};

// ── 5. Teamwork Gears ────────────────────────────────────────────────
export const TeamworkGears: React.FC<Record<string, unknown>> = (props) => {
  const rotation = (props.rotation as number) ?? 0;
  const gears = [
    { cx: 220, cy: 200, r: 60, color: "#3b82f6", label: "Plan", dir: 1 },
    { cx: 350, cy: 200, r: 50, color: "#22c55e", label: "Build", dir: -1 },
    { cx: 460, cy: 200, r: 55, color: "#f59e0b", label: "Deploy", dir: 1 },
  ];
  return (
    <svg viewBox="0 0 650 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <text x="325" y="40" textAnchor="middle" fill="#e0e7ff" fontSize="16" fontWeight="bold">Teamwork — Working Together</text>
      {gears.map((g, i) => (
        <g key={i} transform={`rotate(${rotation * g.dir * 0.5},${g.cx},${g.cy})`}>
          {/* Outer gear teeth */}
          {Array.from({ length: 12 }).map((_, t) => {
            const a = (t / 12) * Math.PI * 2;
            return (
              <rect key={t} x={g.cx + (g.r - 5) * Math.cos(a) - 5} y={g.cy + (g.r - 5) * Math.sin(a) - 5} width="10" height="10" rx="2" fill={g.color}
                    transform={`rotate(${(t / 12) * 360},${g.cx + (g.r - 5) * Math.cos(a)},${g.cy + (g.r - 5) * Math.sin(a)})`} />
            );
          })}
          {/* Gear body */}
          <circle cx={g.cx} cy={g.cy} r={g.r - 10} fill={g.color} opacity="0.7" />
          <circle cx={g.cx} cy={g.cy} r={g.r * 0.3} fill="#0f172a" />
          <text x={g.cx} y={g.cy + 4} textAnchor="middle" fill="#fff" fontSize="11" fontWeight="bold">{g.label}</text>
        </g>
      ))}
      {/* Connection descriptions */}
      <text x="285" y="280" textAnchor="middle" fill="#94a3b8" fontSize="10">sync</text>
      <text x="405" y="280" textAnchor="middle" fill="#94a3b8" fontSize="10">sync</text>
      <text x="325" y="350" textAnchor="middle" fill="#94a3b8" fontSize="12">Interlocking gears = coordinated effort</text>
    </svg>
  );
};

// ── 6. Leadership Pyramid ────────────────────────────────────────────
export const LeadershipPyramid: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  const levels = [
    { label: "Vision", color: "#6366f1", w: 100 },
    { label: "Strategy", color: "#3b82f6", w: 200 },
    { label: "Management", color: "#22c55e", w: 300 },
    { label: "Execution", color: "#f59e0b", w: 400 },
    { label: "Foundation", color: "#ef4444", w: 500 },
  ];
  return (
    <svg viewBox="0 0 600 450" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <text x="300" y="30" textAnchor="middle" fill="#e0e7ff" fontSize="16" fontWeight="bold">Leadership Pyramid</text>
      {levels.map((lv, i) => {
        const y = 60 + i * 70;
        const isActive = i === activeIndex;
        return (
          <g key={i} opacity={isActive ? 1 : 0.4}>
            <rect x={300 - lv.w / 2} y={y} width={lv.w} height="55" rx="6" fill={lv.color} stroke={isActive ? "#fff" : "none"} strokeWidth={isActive ? 2 : 0}>
              {isActive && <animate attributeName="opacity" values="0.7;1;0.7" dur="1.5s" repeatCount="indefinite" />}
            </rect>
            <text x="300" y={y + 32} textAnchor="middle" fill="#fff" fontSize={isActive ? 15 : 13} fontWeight="bold">{lv.label}</text>
          </g>
        );
      })}
      {/* Arrow showing bottom-up support */}
      <line x1="560" y1="400" x2="560" y2="80" stroke="#94a3b8" strokeWidth="1.5" markerEnd="url(#pyramidArrow)" strokeDasharray="4 3" />
      <text x="575" y="240" fill="#94a3b8" fontSize="9" transform="rotate(90,575,240)">Support flows up</text>
      <defs>
        <marker id="pyramidArrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="#94a3b8" />
        </marker>
      </defs>
    </svg>
  );
};

// ── 7. Innovation Spark ──────────────────────────────────────────────
export const InnovationSpark: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  return (
    <svg viewBox="0 0 500 450" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Light bulb */}
      <circle cx="250" cy="180" r="70" fill="#fbbf24" opacity="0.15">
        <animate attributeName="r" values="65;75;65" dur="2s" repeatCount="indefinite" />
      </circle>
      <circle cx="250" cy="180" r="50" fill="#fbbf24" opacity="0.3">
        <animate attributeName="r" values="48;55;48" dur="1.5s" repeatCount="indefinite" />
      </circle>
      {/* Bulb shape */}
      <path d="M220,200 Q220,140 250,120 Q280,140 280,200 L275,210 Q262,215 250,215 Q238,215 225,210 Z" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2" />
      {/* Filament */}
      <path d="M238,180 Q245,165 250,180 Q255,195 262,180" fill="none" stroke="#f59e0b" strokeWidth="2" />
      {/* Base */}
      <rect x="230" y="215" width="40" height="20" rx="4" fill="#6b7280" />
      <line x1="232" y1="221" x2="268" y2="221" stroke="#475569" strokeWidth="1" />
      <line x1="232" y1="228" x2="268" y2="228" stroke="#475569" strokeWidth="1" />
      {/* Rays */}
      {Array.from({ length: 8 }).map((_, i) => {
        const a = (i / 8) * Math.PI * 2;
        const len = 15 + Math.sin(phase * 0.8 + i) * 8;
        return (
          <line key={i} x1={250 + 75 * Math.cos(a)} y1={180 + 75 * Math.sin(a)}
                x2={250 + (75 + len) * Math.cos(a)} y2={180 + (75 + len) * Math.sin(a)}
                stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
        );
      })}
      {/* Sparks */}
      {[0, 1, 2, 3].map((i) => {
        const a = (phase * 0.5 + i * Math.PI / 2) % (Math.PI * 2);
        const d = 95 + Math.sin(phase * 0.3 + i) * 10;
        return (
          <circle key={i} cx={250 + d * Math.cos(a)} cy={180 + d * Math.sin(a)} r="3" fill="#fbbf24" opacity="0.6">
            <animate attributeName="r" values="2;4;2" dur={`${0.8 + i * 0.2}s`} repeatCount="indefinite" />
          </circle>
        );
      })}
      {/* Words radiating */}
      <text x="160" y="300" fill="#94a3b8" fontSize="10" opacity={0.3 + Math.sin(phase * 0.3) * 0.2}>Create</text>
      <text x="250" y="310" textAnchor="middle" fill="#94a3b8" fontSize="10" opacity={0.3 + Math.sin(phase * 0.3 + 1) * 0.2}>Imagine</text>
      <text x="320" y="300" fill="#94a3b8" fontSize="10" opacity={0.3 + Math.sin(phase * 0.3 + 2) * 0.2}>Invent</text>
      <text x="250" y="400" textAnchor="middle" fill="#fbbf24" fontSize="16" fontWeight="bold">Innovation</text>
    </svg>
  );
};

// ── 8. Life Cycle ────────────────────────────────────────────────────
export const LifeCycle: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  const stages = [
    { label: "Birth 🌱", angle: -90, color: "#22c55e" },
    { label: "Growth 📈", angle: -30, color: "#3b82f6" },
    { label: "Maturity ⭐", angle: 30, color: "#f59e0b" },
    { label: "Decline 📉", angle: 90, color: "#ef4444" },
    { label: "Renewal 🔄", angle: 150, color: "#8b5cf6" },
    { label: "Legacy 🏛", angle: 210, color: "#6366f1" },
  ];
  const cx = 300;
  const cy = 200;
  const r = 120;
  return (
    <svg viewBox="0 0 600 430" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <text x="300" y="30" textAnchor="middle" fill="#e0e7ff" fontSize="16" fontWeight="bold">Life Cycle</text>
      {/* Circle */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#334155" strokeWidth="2" strokeDasharray="6 4" />
      {/* Arrow on circle */}
      <path d={`M${cx},${cy - r} A${r},${r} 0 1 1 ${cx - 1},${cy - r}`} fill="none" stroke="#475569" strokeWidth="1.5" markerEnd="url(#lcArrow)" />
      {/* Stages */}
      {stages.map((s, i) => {
        const rad = (s.angle * Math.PI) / 180;
        const sx = cx + r * Math.cos(rad);
        const sy = cy + r * Math.sin(rad);
        const isActive = i === activeIndex;
        return (
          <g key={i}>
            <circle cx={sx} cy={sy} r={isActive ? 28 : 22} fill={s.color} opacity={isActive ? 0.9 : 0.3} stroke={isActive ? "#fff" : "none"} strokeWidth={isActive ? 2 : 0}>
              {isActive && <animate attributeName="r" values="24;30;24" dur="1.5s" repeatCount="indefinite" />}
            </circle>
            <text x={sx} y={sy + 4} textAnchor="middle" fill="#fff" fontSize={isActive ? 11 : 9} fontWeight="bold">{s.label.split(" ")[0]}</text>
            {/* Extended label */}
            <text x={cx + (r + 50) * Math.cos(rad)} y={cy + (r + 50) * Math.sin(rad) + 4} textAnchor="middle" fill={isActive ? s.color : "#475569"} fontSize="10">{s.label}</text>
          </g>
        );
      })}
      <defs>
        <marker id="lcArrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="#475569" />
        </marker>
      </defs>
    </svg>
  );
};
